import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getTransactionSummaryAPI } from "../../../../Store/Action/orderAction";

// ─── Constants ────────────────────────────────────────────────────────────────
const COUNTDOWN_SECONDS = 5 * 60; // 300 s

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) =>
  v !== undefined && v !== null && v !== ""
    ? Number(v).toLocaleString("en-IN")
    : "—";

const pad = (n) => String(n).padStart(2, "0");
const formatCountdown = (secs) =>
  `${pad(Math.floor(secs / 60))}:${pad(secs % 60)}`;

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    fontFamily: "'Segoe UI', sans-serif",
  },
  card: {
    background: "#fff",
    borderRadius: 18,
    width: "min(480px, 95vw)",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 10px 35px rgba(0,0,0,0.08)",
    border: "1px solid #f0eadf",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: "linear-gradient(135deg, #9c7726 0%, #b68a2d 100%)",
    padding: "18px 20px",
    borderRadius: "16px 16px 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    color: "#fff",
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
    letterSpacing: 0.3,
  },
  backBtn: {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: 8,
    color: "#fff",
    width: 32,
    height: 32,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  timerPill: {
    background: "#fff",
    border: "1px solid #9c7726",
    borderRadius: 24,
    padding: "5px 14px",
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#9c7726",
    fontWeight: 600,
    fontSize: 14,
    minWidth: 90,
    justifyContent: "center",
  },
  body: { padding: "20px 20px 0" },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginBottom: 16,
  },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: {
    fontSize: 12,
    color: "#727272",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontFamily: "'Poppins', sans-serif",
  },
  valueBox: {
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 15,
    color: "#000",
    fontWeight: 500,
    background: "#fff",
    minHeight: 42,
    display: "flex",
    alignItems: "center",
    fontFamily: "'Poppins', sans-serif",
  },
  valueBoxGold: {
    border: "1px solid #e8d2a2",
    background: "#fbf6ee",
    color: "#9c7726",
  },
  skeleton: {
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
    borderRadius: 6,
    height: 18,
    width: "70%",
  },
  divider: { borderTop: "1px solid #f0f0f0", margin: "4px 0 16px" },
  notesBox: {
    background: "#fbf6ee",
    border: "1px solid #ead7b0",
    borderRadius: 12,
    padding: "14px 16px",
    margin: "0 20px 16px",
    textAlign: "left",
  },
  notesTitle: {
    color: "#9c7726",
    fontWeight: 600,
    fontSize: 14,
    margin: "0 0 10px",
  },
  noteItem: {
    color: "#5a4a2a",
    fontSize: 13,
    lineHeight: 1.6,
    paddingLeft: 14,
    position: "relative",
    margin: "0 0 4px",
  },
  noteDot: {
    position: "absolute",
    left: 0,
    top: "0.5em",
    width: 5,
    height: 5,
    borderRadius: "50%",
    background: "#b8892a",
  },
  refreshRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 0 8px",
    gap: 6,
    color: "#aaa",
    fontSize: 12,
  },
  refreshBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#b8892a",
    fontSize: 13,
    fontWeight: 600,
    padding: 0,
  },
  footer: { padding: "0 20px 20px" },
  continueBtn: {
    width: "100%",
    padding: "14px 0",
    background: "linear-gradient(135deg, #9c7726 0%, #b68a2d 100%)",
    border: "none",
    borderRadius: 10,
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: 0.3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 0.2s ease",
    fontFamily: "'Poppins', sans-serif",
    boxShadow: "0 6px 18px rgba(156, 119, 38, 0.25)",
  },
  errorBox: {
    background: "#fff0f0",
    border: "1px solid #ffcccc",
    borderRadius: 8,
    padding: "12px 16px",
    color: "#c0392b",
    fontSize: 13,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
const RetryPaymentSummary = ({
  orderId: propOrderId,
  userId: propUserId,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  // Support both prop-based (modal) and route-based usage
  const orderId = propOrderId ?? location.state?.orderId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [lastFetch, setLastFetch] = useState(null);

  // Keep a ref so the interval callback always sees latest fetchSummary
  const fetchRef = useRef(null);

  const fetchSummary = useCallback(() => {
    if (!orderId) return;
    setLoading(true);
    setError(null);

    dispatch(
      getTransactionSummaryAPI({ order_id: orderId }, (result) => {
        if (result?.status) {
          setData(result?.data);
          setLastFetch(new Date());
          setCountdown(COUNTDOWN_SECONDS); // reset timer on fresh data
        } else {
          setError(
            result?.message || "Failed to load summary. Please try again.",
          );
        }
        setLoading(false);
      }),
    );
  }, [dispatch, orderId]);

  // Keep ref in sync
  useEffect(() => {
    fetchRef.current = fetchSummary;
  }, [fetchSummary]);

  // Initial fetch
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Countdown tick — also triggers auto-refresh when it hits 0
  useEffect(() => {
    if (loading) return;
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchRef.current?.(); // auto-refresh
          return COUNTDOWN_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [loading]);

  // ── Continue to payment-card ───────────────────────────────────────────────
  const handleContinue = () => {
    navigate("/payment-card", {
      state: {
        isDuePayment: true,
        orderData: data,
        dueAmount: data?.due_amount ?? data?.transaction_amount,
        orderId: orderId,
        userId: propUserId,
      },
    });
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(-1);
  };

  // ── Field aliases — adjust keys to match your actual API response ──────────
  const gold10g = data?.gold_rate;
  const silver1kg = data?.silver_rate;
  const grossWt = data?.total_gross_weight;
  const netWt = data?.total_net_weight;
  const totalFine = data?.total_fine;
  const totalQty = data?.total_qty;
  const orderAmt = data?.order_amount;
  const dueAmt = data?.due_amount;

  // ── Render ─────────────────────────────────────────────────────────────────
  const inner = (
    <div style={S.card} onClick={(e) => e.stopPropagation()}>
      {/* HEADER */}
      <div style={S.header}>
        <button style={S.backBtn} onClick={handleBack}>
          ‹
        </button>
        <h2 style={S.headerTitle}>Total Summary</h2>
        <div style={S.timerPill}>
          <span>⏱</span>
          <span>{formatCountdown(countdown)}</span>
        </div>
      </div>

      {/* BODY */}
      <div style={S.body}>
        <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>

        {error && (
          <div style={S.errorBox}>
            ⚠ {error}
            <button style={S.refreshBtn} onClick={fetchSummary}>
              Retry
            </button>
          </div>
        )}

        {/* Row 1 – Rates */}
        <div style={S.grid}>
          <div style={S.fieldGroup}>
            <span style={S.label}>Gold Rate (10g)</span>
            <div style={{ ...S.valueBox, ...S.valueBoxGold }}>
              {loading ? <div style={S.skeleton} /> : fmt(gold10g)}
            </div>
          </div>
          <div style={S.fieldGroup}>
            <span style={S.label}>Silver Rate (1kg)</span>
            <div style={{ ...S.valueBox, ...S.valueBoxGold }}>
              {loading ? <div style={S.skeleton} /> : fmt(silver1kg)}
            </div>
          </div>
        </div>

        {/* Row 2 – Weights */}
        <div style={S.grid}>
          <div style={S.fieldGroup}>
            <span style={S.label}>Total Gross Wt (g)</span>
            <div style={S.valueBox}>
              {loading ? <div style={S.skeleton} /> : fmt(grossWt)}
            </div>
          </div>
          <div style={S.fieldGroup}>
            <span style={S.label}>Total Net Wt (g)</span>
            <div style={S.valueBox}>
              {loading ? <div style={S.skeleton} /> : fmt(netWt)}
            </div>
          </div>
        </div>

        {/* Row 3 – Fine & Qty */}
        <div style={S.grid}>
          <div style={S.fieldGroup}>
            <span style={S.label}>Total Fine (g)</span>
            <div style={S.valueBox}>
              {loading ? <div style={S.skeleton} /> : fmt(totalFine)}
            </div>
          </div>
          <div style={S.fieldGroup}>
            <span style={S.label}>Total Qty (pc)</span>
            <div style={S.valueBox}>
              {loading ? <div style={S.skeleton} /> : (totalQty ?? "—")}
            </div>
          </div>
        </div>

        {/* Row 4 – Amounts */}
        <div style={S.grid}>
          <div style={S.fieldGroup}>
            <span style={S.label}>Order Amount</span>
            <div style={S.valueBox}>
              {loading ? <div style={S.skeleton} /> : fmt(orderAmt)}
            </div>
          </div>
          <div style={S.fieldGroup}>
            <span style={S.label}>Due Amount</span>
            <div style={{ ...S.valueBox, ...S.valueBoxGold }}>
              {loading ? <div style={S.skeleton} /> : fmt(dueAmt)}
            </div>
          </div>
        </div>

        {/* Last refreshed */}
        {lastFetch && !loading && (
          <div style={S.refreshRow}>
            Rates updated{" "}
            {lastFetch.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
            })}
            &nbsp;·&nbsp;
            <button style={S.refreshBtn} onClick={fetchSummary}>
              Refresh now
            </button>
          </div>
        )}

        <div style={S.divider} />
      </div>

      {/* IMPORTANT NOTES */}
      <div style={S.notesBox}>
        <p style={S.notesTitle}>Important Notes:</p>

        {[
          "Your previous payment was unsuccessful.",
          "The order amount shown is based on the current live gold and silver rates.",
          "These rates are valid for the next 5 minutes only.",
          "Complete your payment before the timer expires to lock in the current price.",
          "Once the timer expires, the amount will be recalculated using the latest market rates and may change.",
        ].map((note, i) => (
          <p key={i} style={S.noteItem}>
            <span style={S.noteDot} />
            {note}
          </p>
        ))}
      </div>

      {/* FOOTER */}
      <div style={S.footer}>
        <button
          style={{ ...S.continueBtn, opacity: loading ? 0.65 : 1 }}
          onClick={handleContinue}
          disabled={loading || !!error}
        >
          Continue →
        </button>
      </div>
    </div>
  );

  return onClose ? (
    <div style={S.overlay} onClick={handleBack}>
      {inner}
    </div>
  ) : (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      {inner}
    </div>
  );
};

export default RetryPaymentSummary;
